/**
 * ScreenshotMaskingEditor — 스크린샷 주석/마스킹 편집기
 *
 * 두 가지 모드:
 * - mode="mask" (기본): 민감 정보 가리기 전용 (사각형 마스크 + undo/전체삭제). 일반 피드백 폼에서 사용.
 * - mode="annotate": QA 피드백용 주석 도구 세트
 *     🖊️ 펜(자유선) · ➡️ 화살표 · ▭ 사각형 테두리 · 🔤 텍스트 · 🙈 마스킹(가리기)
 *     🎨 색상(기본 빨강) · ↩️ 실행 취소 · 🗑️ 전체 지우기
 *
 * 캔버스 자체 구현(라이브러리 추가 없음). 모든 주석은 이미지 원본 좌표계로 저장하여
 * 표시(축소)와 내보내기(원본 해상도)가 동일 로직으로 그려진다.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { t, Trans } from '@lingui/macro';
import { Undo2, Trash2, Pen, ArrowUpRight, Square, Type, EyeOff } from 'lucide-react';

export type AnnotateMode = 'mask' | 'annotate';
type Tool = 'mask' | 'pen' | 'arrow' | 'rect' | 'text';

interface Point { x: number; y: number; }

type Annotation =
  | { type: 'mask'; x: number; y: number; w: number; h: number }
  | { type: 'rect'; x: number; y: number; w: number; h: number; color: string }
  | { type: 'pen'; points: Point[]; color: string }
  | { type: 'arrow'; x1: number; y1: number; x2: number; y2: number; color: string }
  | { type: 'text'; x: number; y: number; text: string; color: string };

interface Props {
  imageBlob: Blob;
  onConfirm: (editedBlob: Blob) => void;
  onCancel: () => void;
  /** 'mask'(기본): 가리기 전용 / 'annotate': 전체 주석 도구 */
  mode?: AnnotateMode;
}

// 색상 팔레트 (빨강 기본)
const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#ffffff', '#111827'];

// 도구 정의 (annotate 모드 툴바)
const TOOLS: { key: Tool; icon: typeof Pen }[] = [
  { key: 'pen', icon: Pen },
  { key: 'arrow', icon: ArrowUpRight },
  { key: 'rect', icon: Square },
  { key: 'text', icon: Type },
  { key: 'mask', icon: EyeOff },
];

export const ScreenshotMaskingEditor: React.FC<Props> = ({ imageBlob, onConfirm, onCancel, mode = 'mask' }) => {
  const isAnnotate = mode === 'annotate';
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [draft, setDraft] = useState<Annotation | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  // annotate 모드 선택 도구/색상. mask 모드는 항상 'mask' 도구.
  const [tool, setTool] = useState<Tool>(isAnnotate ? 'pen' : 'mask');
  const [color, setColor] = useState<string>(COLORS[0]);

  // 텍스트 입력 대기 상태 (도구=text 로 클릭 시)
  const [pendingText, setPendingText] = useState<{ ix: number; iy: number; screenX: number; screenY: number } | null>(null);
  const [textValue, setTextValue] = useState('');
  const textInputRef = useRef<HTMLInputElement>(null);

  // 캔버스 표시용 스케일 (이미지 → 캔버스)
  const scaleRef = useRef({ scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0, dispW: 0, dispH: 0 });
  // 주석 굵기/폰트 크기 (이미지 원본 px 기준)
  const strokeWRef = useRef(4);
  const fontSizeRef = useRef(24);

  // ── 이미지 로드 ──
  useEffect(() => {
    const url = URL.createObjectURL(imageBlob);
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      strokeWRef.current = Math.max(3, Math.round(img.naturalWidth / 400));
      fontSizeRef.current = Math.max(16, Math.round(img.naturalWidth / 45));
      setImageLoaded(true);
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [imageBlob]);

  // ── 주석 하나를 이미지 좌표계로 그림 (표시/내보내기 공용) ──
  const paintAnnotation = useCallback((ctx: CanvasRenderingContext2D, ann: Annotation, preview: boolean) => {
    const strokeW = strokeWRef.current;
    switch (ann.type) {
      case 'mask': {
        ctx.fillStyle = preview ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,1)';
        ctx.fillRect(ann.x, ann.y, ann.w, ann.h);
        if (preview) {
          ctx.strokeStyle = 'rgba(239,68,68,0.8)';
          ctx.lineWidth = strokeW;
          ctx.strokeRect(ann.x, ann.y, ann.w, ann.h);
        }
        break;
      }
      case 'rect': {
        ctx.strokeStyle = ann.color;
        ctx.lineWidth = strokeW;
        ctx.strokeRect(ann.x, ann.y, ann.w, ann.h);
        break;
      }
      case 'pen': {
        if (ann.points.length < 2) break;
        ctx.strokeStyle = ann.color;
        ctx.lineWidth = strokeW;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(ann.points[0].x, ann.points[0].y);
        for (let i = 1; i < ann.points.length; i++) ctx.lineTo(ann.points[i].x, ann.points[i].y);
        ctx.stroke();
        break;
      }
      case 'arrow': {
        ctx.strokeStyle = ann.color;
        ctx.fillStyle = ann.color;
        ctx.lineWidth = strokeW;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(ann.x1, ann.y1);
        ctx.lineTo(ann.x2, ann.y2);
        ctx.stroke();
        // 화살촉
        const angle = Math.atan2(ann.y2 - ann.y1, ann.x2 - ann.x1);
        const head = strokeW * 4;
        ctx.beginPath();
        ctx.moveTo(ann.x2, ann.y2);
        ctx.lineTo(ann.x2 - head * Math.cos(angle - Math.PI / 6), ann.y2 - head * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(ann.x2 - head * Math.cos(angle + Math.PI / 6), ann.y2 - head * Math.sin(angle + Math.PI / 6));
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'text': {
        const fs = fontSizeRef.current;
        ctx.font = `bold ${fs}px sans-serif`;
        ctx.textBaseline = 'top';
        // 가독성 위해 외곽선
        ctx.lineWidth = Math.max(2, fs / 8);
        ctx.strokeStyle = 'rgba(0,0,0,0.7)';
        ctx.strokeText(ann.text, ann.x, ann.y);
        ctx.fillStyle = ann.color;
        ctx.fillText(ann.text, ann.x, ann.y);
        break;
      }
    }
  }, []);

  // ── 캔버스 그리기 (표시) ──
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const container = canvas.parentElement;
    if (!container) return;

    const maxW = container.clientWidth;
    const maxH = container.clientHeight;
    const imgRatio = img.naturalWidth / img.naturalHeight;
    const containerRatio = maxW / maxH;

    let dispW: number, dispH: number;
    if (imgRatio > containerRatio) {
      dispW = maxW;
      dispH = maxW / imgRatio;
    } else {
      dispH = maxH;
      dispW = maxH * imgRatio;
    }
    const offsetX = (maxW - dispW) / 2;
    const offsetY = (maxH - dispH) / 2;

    canvas.width = maxW;
    canvas.height = maxH;
    const scaleX = img.naturalWidth / dispW;
    const scaleY = img.naturalHeight / dispH;
    scaleRef.current = { scaleX, scaleY, offsetX, offsetY, dispW, dispH };

    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, offsetX, offsetY, dispW, dispH);

    // 이미지 좌표계로 변환 후 주석 그리기 (표시/내보내기 공용 로직)
    ctx.save();
    ctx.translate(offsetX, offsetY);
    ctx.scale(dispW / img.naturalWidth, dispH / img.naturalHeight);
    for (const ann of annotations) paintAnnotation(ctx, ann, false);
    if (draft) paintAnnotation(ctx, draft, true);
    ctx.restore();
  }, [annotations, draft, paintAnnotation]);

  useEffect(() => {
    if (imageLoaded) drawCanvas();
  }, [imageLoaded, drawCanvas]);

  useEffect(() => {
    const handleResize = () => { if (imageLoaded) drawCanvas(); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imageLoaded, drawCanvas]);

  // ── 좌표 변환 ──
  const toImageCoords = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { ix: 0, iy: 0 };
    const rect = canvas.getBoundingClientRect();
    const { scaleX, scaleY, offsetX, offsetY } = scaleRef.current;
    const ix = (clientX - rect.left - offsetX) * scaleX;
    const iy = (clientY - rect.top - offsetY) * scaleY;
    return { ix, iy };
  };

  const activeTool: Tool = isAnnotate ? tool : 'mask';

  // ── 포인터 핸들러 ──
  const handlePointerDown = (e: React.PointerEvent) => {
    // 텍스트 입력 대기 중이면 먼저 커밋
    if (pendingText) { commitText(); return; }
    e.preventDefault();

    const { ix, iy } = toImageCoords(e.clientX, e.clientY);

    if (activeTool === 'text') {
      setPendingText({ ix, iy, screenX: e.clientX, screenY: e.clientY });
      setTextValue('');
      setTimeout(() => textInputRef.current?.focus(), 0);
      return;
    }

    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setIsDrawing(true);

    if (activeTool === 'pen') {
      setDraft({ type: 'pen', points: [{ x: ix, y: iy }], color });
    } else if (activeTool === 'arrow') {
      setDraft({ type: 'arrow', x1: ix, y1: iy, x2: ix, y2: iy, color });
    } else if (activeTool === 'rect') {
      setDraft({ type: 'rect', x: ix, y: iy, w: 0, h: 0, color });
    } else {
      setDraft({ type: 'mask', x: ix, y: iy, w: 0, h: 0 });
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDrawing || !draft) return;
    e.preventDefault();
    const { ix, iy } = toImageCoords(e.clientX, e.clientY);
    if (draft.type === 'pen') {
      setDraft({ ...draft, points: [...draft.points, { x: ix, y: iy }] });
    } else if (draft.type === 'arrow') {
      setDraft({ ...draft, x2: ix, y2: iy });
    } else if (draft.type === 'rect' || draft.type === 'mask') {
      setDraft({ ...draft, w: ix - draft.x, h: iy - draft.y });
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (draft) {
      if (draft.type === 'mask' || draft.type === 'rect') {
        if (Math.abs(draft.w) > 5 && Math.abs(draft.h) > 5) {
          const normalized = {
            ...draft,
            x: draft.w < 0 ? draft.x + draft.w : draft.x,
            y: draft.h < 0 ? draft.y + draft.h : draft.y,
            w: Math.abs(draft.w),
            h: Math.abs(draft.h),
          };
          setAnnotations(prev => [...prev, normalized]);
        }
      } else if (draft.type === 'arrow') {
        if (Math.hypot(draft.x2 - draft.x1, draft.y2 - draft.y1) > 8) {
          setAnnotations(prev => [...prev, draft]);
        }
      } else if (draft.type === 'pen') {
        if (draft.points.length > 1) setAnnotations(prev => [...prev, draft]);
      }
    }
    setDraft(null);
    setIsDrawing(false);
  };

  // ── 텍스트 커밋 ──
  const commitText = useCallback(() => {
    if (pendingText && textValue.trim()) {
      setAnnotations(prev => [...prev, {
        type: 'text', x: pendingText.ix, y: pendingText.iy, text: textValue.trim(), color,
      }]);
    }
    setPendingText(null);
    setTextValue('');
  }, [pendingText, textValue, color]);

  // ── 확인: 주석이 적용된 최종 이미지 생성 ──
  const handleConfirm = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;
    // 대기 중인 텍스트가 있으면 반영
    const finalAnns = [...annotations];
    if (pendingText && textValue.trim()) {
      finalAnns.push({ type: 'text', x: pendingText.ix, y: pendingText.iy, text: textValue.trim(), color });
    }

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = img.naturalWidth;
    exportCanvas.height = img.naturalHeight;
    const ctx = exportCanvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    for (const ann of finalAnns) paintAnnotation(ctx, ann, false);

    exportCanvas.toBlob((blob) => { if (blob) onConfirm(blob); }, 'image/png');
  }, [annotations, pendingText, textValue, color, paintAnnotation, onConfirm]);

  const hasAnnotations = annotations.length > 0;
  const toolLabel = (k: Tool): string => k === 'pen' ? t`Pen` : k === 'arrow' ? t`Arrow` : k === 'rect' ? t`Rectangle` : k === 'text' ? t`Text` : t`Mask`;

  return (
    <div
      data-screenshot-exclude
      className="fixed inset-0 bg-[#0f0f1a] flex flex-col"
      style={{ zIndex: 100000 }}
    >
      {/* ── 상단 헤더 바 ── */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#1e1e2e] border-b border-white/10 shadow-lg gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-white font-medium text-sm whitespace-nowrap">
            {isAnnotate ? t`✏️ Mark the problem area` : t`🖌️ Drag to mask sensitive information`}
          </span>
          {hasAnnotations && (
            <span className="text-xs text-white/50 bg-white/10 px-2 py-0.5 rounded-full whitespace-nowrap">
              <Trans>{annotations.length} items</Trans>
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {/* annotate 모드: 도구 + 색상 팔레트 */}
          {isAnnotate && (
            <>
              <div className="flex items-center gap-0.5 mr-1">
                {TOOLS.map(({ key, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setTool(key)}
                    title={toolLabel(key)}
                    className={`p-2 rounded-lg transition-colors ${
                      tool === key ? 'bg-blue-600 text-white' : 'hover:bg-white/10 text-white/70 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 mr-1">
                {COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    title={t`Color ${c}`}
                    className={`w-5 h-5 rounded-full border transition-transform ${
                      color === c ? 'border-white scale-110 ring-2 ring-white/40' : 'border-white/30'
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
              <div className="w-px h-5 bg-white/20 mx-1" />
            </>
          )}

          {/* 실행 취소 */}
          <button
            onClick={() => setAnnotations(prev => prev.slice(0, -1))}
            disabled={!hasAnnotations}
            className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors disabled:opacity-20 disabled:hover:bg-transparent"
            title={t`Undo`}
          >
            <Undo2 className="w-4 h-4" />
          </button>
          {/* 전체 삭제 */}
          <button
            onClick={() => setAnnotations([])}
            disabled={!hasAnnotations}
            className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors disabled:opacity-20 disabled:hover:bg-transparent"
            title={t`Clear all`}
          >
            <Trash2 className="w-4 h-4" />
          </button>

          <div className="w-px h-5 bg-white/20 mx-1" />

          <button
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg hover:bg-white/10 text-white/70 hover:text-white text-sm transition-colors"
            title={t`Close`}
          >
            <Trans>Close</Trans>
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors"
            title={t`Confirm`}
          >
            <Trans>Confirm</Trans>
          </button>
        </div>
      </div>

      {/* ── 캔버스 영역 ── */}
      <div className="flex-1 relative overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full h-full"
          style={{ cursor: activeTool === 'text' ? 'text' : 'crosshair', touchAction: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        />

        {/* 텍스트 입력 오버레이 */}
        {pendingText && (
          <input
            ref={textInputRef}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitText();
              if (e.key === 'Escape') { setPendingText(null); setTextValue(''); }
            }}
            onBlur={commitText}
            placeholder={t`Type text then Enter`}
            className="absolute px-2 py-1 rounded bg-black/70 text-white text-sm outline-none border border-white/40 min-w-[160px]"
            style={{
              left: Math.min(pendingText.screenX, window.innerWidth - 180),
              top: pendingText.screenY,
            }}
          />
        )}
      </div>
    </div>
  );
};
